/**
 * SCWT-PM-V2 Phase 7 · Slice E — Property Manager · Water Health tab.
 *
 * Manual invite flow (agency-sponsored / paid-by-PM is deferred).
 * Server-authoritative — this component NEVER shows stripe_customer_id,
 * subscription_id, session_id, invoice_id or payment method info even
 * if the payload accidentally contained them (the backend redaction
 * already scrubs these before wire; this is belt+braces).
 */
import { useEffect, useState } from "react";
import { RefreshCw, Send, XCircle, Mail, Info, Home, Clock, User, Wrench } from "lucide-react";
import api from "@/lib/api";
import { LWH_LANDLORD_STATUS_LABEL, LWH_LANDLORD_STATUS_TONE, fmtDateAU } from "@/lib/lwhStatus";

// Fields the PM must never see — used as a defensive filter before render.
const FORBIDDEN = new Set([
  "stripe_customer_id", "stripe_subscription_id", "stripe_checkout_session_id",
  "stripe_latest_invoice_id", "stripe_price_id", "stripe_events_seen",
  "unit_amount_cents", "audit_log", "last_stripe_event_id",
]);

export default function PmWaterHealthTab() {
  const [items, setItems]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);
  const [showInvite, setInvite] = useState(false);
  const [inviteNotice, setInviteNotice] = useState(null);
  const [busyId, setBusy]   = useState(null);

  const load = async () => {
    setLoad(true); setError(null);
    try {
      const { data } = await api.get("/property-manager/water-health/memberships");
      setItems((data.items || []).map((r) => Object.fromEntries(
        Object.entries(r).filter(([k]) => !FORBIDDEN.has(k)),
      )));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load memberships.");
    } finally { setLoad(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <section data-testid="pm-tab-body-water-health" className="mt-2">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900">
            Water Health Program
          </h2>
          <p className="mt-1.5 text-sm text-slate-600 max-w-2xl leading-relaxed">
            Invite landlords to enrol properties in the AWARE Water Health
            Program. The landlord pays AWARE directly. You don&apos;t see card
            details, subscription IDs, or payment info — you only see status
            and renewal dates.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button type="button" data-testid="pm-water-health-refresh"
                    onClick={load}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 rounded-full px-3 py-2">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button type="button" data-testid="pm-water-health-invite-btn"
                    onClick={() => setInvite(true)}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold px-4 py-2 shadow-sm">
            <Send className="w-4 h-4" /> Invite landlord
          </button>
        </div>
      </div>

      {inviteNotice && (
        <div data-testid="pm-water-health-invite-notice"
              className={`mb-4 rounded-2xl border p-4 text-sm ${
                inviteNotice.ok
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-amber-200 bg-amber-50 text-amber-800"
              }`}>
          {inviteNotice.message}
        </div>
      )}

      {loading && <div className="text-sm text-slate-500 py-10 text-center">Loading…</div>}
      {!loading && error && (
        <div data-testid="pm-water-health-error"
              className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
      )}
      {!loading && !error && items.length === 0 && (
        <div data-testid="pm-water-health-empty"
              className="rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center">
          <Home className="w-9 h-9 text-cyan-500 mx-auto mb-3" />
          <div className="font-display font-semibold text-slate-900">
            No memberships in your portfolio yet
          </div>
          <p className="text-slate-500 text-sm mt-1.5 max-w-md mx-auto">
            Send a landlord an invitation to enrol a property.
          </p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <ul data-testid="pm-water-health-list" className="grid gap-3">
          {items.map((row) => (
            <PmMembershipRow key={row.id} row={row} />
          ))}
        </ul>
      )}

      {showInvite && <PmInviteDrawer onClose={() => setInvite(false)}
                                       onSent={(emailStatus) => {
                                         setInvite(false);
                                         setInviteNotice(emailStatus === "sent"
                                           ? { ok: true, message: "Invitation created — the enrolment email has been sent to the landlord." }
                                           : { ok: false, message: "Invitation created, but the email could not be delivered. Contact AWARE support to resend it." });
                                         load();
                                       }} />}
    </section>
  );
}

function PmMembershipRow({ row }) {
  const tone = LWH_LANDLORD_STATUS_TONE[row.status] || "bg-slate-100 text-slate-700 border-slate-200";
  const label = LWH_LANDLORD_STATUS_LABEL[row.status] || row.status;
  return (
    <li data-testid={`pm-water-health-row-${row.id}`}
          className="bg-white rounded-2xl border border-slate-200 hover:border-cyan-200 hover:shadow transition-all p-4 sm:p-5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-xs sm:text-sm text-slate-500">{row.reference}</span>
        <span data-testid={`pm-water-health-status-${row.id}`}
               className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full border px-2.5 py-1 ${tone}`}>
          {label}
        </span>
        {row.has_pending_invitation && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700">
            <Mail className="w-3 h-3" /> Invitation pending
          </span>
        )}
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Property</div>
          <div className="mt-0.5 text-slate-900 font-semibold">{row.property_id}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Renewal date</div>
          <div className="mt-0.5 text-slate-700">{fmtDateAU(row.current_period_end)}</div>
        </div>
        {row.payer_type && (
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Payer</div>
            <div className="mt-0.5 text-slate-700 capitalize">{row.payer_type.replace(/_/g, " ")}</div>
          </div>
        )}
        {row.cancel_at_period_end && (
          <div className="sm:col-span-2 rounded-xl bg-orange-50 border border-orange-200 p-3 text-xs text-orange-800">
            Landlord has scheduled cancellation. Benefits continue until {fmtDateAU(row.current_period_end)}.
          </div>
        )}
      </div>
    </li>
  );
}

function PmInviteDrawer({ onClose, onSent }) {
  const [properties, setProps] = useState([]);
  const [propId, setPropId]    = useState("");
  const [note, setNote]        = useState("");
  const [saving, setSaving]    = useState(false);
  const [error, setError]      = useState(null);

  useEffect(() => {
    api.get("/property-manager/properties")
      .then(({ data }) => setProps((data.items || []).map((it) => ({
        id: it.property?.id, address: it.property?.address,
        landlord_id: it.property?.landlord_id,
      })).filter((p) => p.id && p.landlord_id)))
      .catch(() => setProps([]));
  }, []);

  const send = async (e) => {
    e?.preventDefault();
    if (!propId) { setError("Choose a property."); return; }
    const p = properties.find((x) => x.id === propId);
    if (!p?.landlord_id) { setError("This property has no linked landlord yet."); return; }
    setSaving(true); setError(null);
    try {
      const { data } = await api.post("/property-manager/water-health/invitations",
        { property_id: propId, landlord_id: p.landlord_id, note });
      onSent?.(data?.invitation_email_status);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.detail || "Could not send invitation."));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="pm-water-health-invite-drawer"
          className="fixed inset-0 z-50 flex justify-end bg-slate-900/50"
          onClick={onClose}>
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-slate-900">
            Invite landlord to enrol
          </h3>
          <button type="button" data-testid="pm-water-health-invite-close"
                    onClick={onClose} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100">
            <XCircle className="w-5 h-5" />
          </button>
        </header>
        <form onSubmit={send} className="p-5 space-y-5">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600">Property</label>
            <select data-testid="pm-water-health-invite-property"
                    value={propId} onChange={(e) => setPropId(e.target.value)}
                    className="mt-1.5 w-full h-11 rounded-xl border border-slate-300 px-3 text-sm">
              <option value="">Choose a managed property…</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.address}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600">Note to landlord (optional)</label>
            <textarea data-testid="pm-water-health-invite-note"
                       value={note} onChange={(e) => setNote(e.target.value)}
                       maxLength={500} rows={3}
                       className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm resize-none"
                       placeholder="e.g. Recommended after your recent test results."/>
          </div>
          <div className="rounded-xl bg-cyan-50 border border-cyan-200 p-3 text-[11px] text-cyan-900 leading-relaxed">
            <strong className="font-semibold">You are inviting only.</strong> The landlord pays AWARE directly ($79/year), and the water test itself is charged separately. Membership is optional. Sending this invitation does not make the agency financially responsible.
          </div>
          {error && (
            <div data-testid="pm-water-health-invite-error"
                  className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800">{error}</div>
          )}
          <button type="submit" data-testid="pm-water-health-invite-submit"
                    disabled={saving}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white font-semibold py-2.5">
            <Send className="w-4 h-4" /> {saving ? "Sending…" : "Send invitation"}
          </button>
        </form>
      </div>
    </div>
  );
}
